import { Component } from '@theme/component';

/**
 * EFS language switcher (snippets/efs-language-selector.liquid): flag button + listbox of
 * shop languages. Choosing a language submits Shopify's localization form.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLUListElement} list
 * @property {HTMLInputElement} input
 * @property {HTMLLIElement[]} [options]
 *
 * @extends {Component<Refs>}
 */
export class EfsLanguageSelector extends Component {
  requiredRefs = ['form', 'trigger', 'panel', 'list', 'input'];

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeOpenListeners();
  }

  get isOpen() {
    return !this.refs.panel.hidden;
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;

    this.refs.panel.hidden = false;
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('focusout', this.#onFocusOut);

    const options = this.refs.options ?? [];
    const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
    (selected ?? options[0])?.focus();
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;

    this.refs.panel.hidden = true;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.#removeOpenListeners();

    if (restoreFocus) this.refs.trigger.focus();
  }

  /**
   * Submits the localization form with the clicked language.
   * @param {MouseEvent | KeyboardEvent} event
   */
  select(event) {
    const option = /** @type {HTMLElement} */ (event.target).closest('[role="option"]');
    const code = option instanceof HTMLElement ? option.dataset.languageCode : undefined;
    if (!code) return;

    if (code === this.refs.input.value) {
      this.close({ restoreFocus: true });
      return;
    }

    for (const item of this.refs.options ?? []) {
      item.setAttribute('aria-selected', String(item === option));
    }

    this.refs.input.value = code;
    this.close();
    this.refs.form.submit();
  }

  /**
   * Arrow keys, Home/End move between options; Enter/Space select.
   * @param {KeyboardEvent} event
   */
  onListKeydown(event) {
    const options = this.refs.options ?? [];
    const current = options.indexOf(/** @type {HTMLLIElement} */ (document.activeElement));
    let next = -1;

    switch (event.key) {
      case 'ArrowDown':
        next = Math.min(current + 1, options.length - 1);
        break;
      case 'ArrowUp':
        next = Math.max(current - 1, 0);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (current !== -1) this.select(/** @type {any} */ ({ target: options[current] }));
        return;
      default:
        return;
    }

    event.preventDefault();
    options[next]?.focus();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close({ restoreFocus: true });
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.close();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    // Focus left the component (Tab away) — not a click on an option, which keeps focus inside.
    if (event.relatedTarget) this.close();
  };

  #removeOpenListeners() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('focusout', this.#onFocusOut);
  }
}

if (!customElements.get('efs-language-selector')) {
  customElements.define('efs-language-selector', EfsLanguageSelector);
}
